import { RouteWithGates } from '../api/routes';
import { Gate } from '../types/Gate';

/**
 * What "unused" means on the Route Management page.
 *
 * A gate is unused when no session uses it. A route is unused when no session
 * uses it and every gate on it is unused — only then can the route itself go.
 * A route that is in use can still carry unused gates, and its session count
 * says nothing about them, so they are flagged gate by gate.
 */

export function isGateUnused(gate: Gate): boolean {
  return !gate.sessions || gate.sessions.length === 0;
}

export function unusedGatesOf(route: RouteWithGates): Gate[] {
  return (route.gates ?? []).filter(isGateUnused);
}

export function isRouteUnused(route: RouteWithGates): boolean {
  return route.total_session_count === 0 && (route.gates ?? []).every(isGateUnused);
}

/** The route itself, or at least one of its gates, can be deleted. */
export function routeHasUnused(route: RouteWithGates): boolean {
  return isRouteUnused(route) || unusedGatesOf(route).length > 0;
}

export interface UnusedDeletePlan {
  /** Routes deleted outright. Their gates are in `gateIds` as well. */
  routeIds: string[];
  /** Every gate to delete: those on the deleted routes, and the unused ones on routes that stay. */
  gateIds: string[];
  /** Routes that stay because they are in use, but lose unused gates. */
  trimmedRouteCount: number;
  /** How many of `gateIds` sit on a route that stays. */
  trimmedGateCount: number;
  /** Routes offered that had nothing unused on them. */
  skippedRouteCount: number;
}

/**
 * Work out what deleting the unused parts of these routes would remove. The
 * same plan serves the selected routes and the whole team, so the button
 * label, the confirm and the deletion itself always agree.
 */
export function planUnusedDelete(routes: RouteWithGates[]): UnusedDeletePlan {
  const plan: UnusedDeletePlan = {
    routeIds: [], gateIds: [], trimmedRouteCount: 0, trimmedGateCount: 0, skippedRouteCount: 0,
  };
  for (const route of routes) {
    if (isRouteUnused(route)) {
      plan.routeIds.push(route.id);
      for (const gate of route.gates ?? []) plan.gateIds.push(gate.id);
      continue;
    }
    const unused = unusedGatesOf(route);
    if (unused.length === 0) {
      plan.skippedRouteCount += 1;
      continue;
    }
    plan.trimmedRouteCount += 1;
    plan.trimmedGateCount += unused.length;
    for (const gate of unused) plan.gateIds.push(gate.id);
  }
  return plan;
}

export function planIsEmpty(plan: UnusedDeletePlan): boolean {
  return plan.routeIds.length === 0 && plan.gateIds.length === 0;
}

function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/** "1 route, 3 gates" — for a button label. */
export function summariseUnusedDelete(plan: UnusedDeletePlan): string {
  return `${count(plan.routeIds.length, 'route')}, ${count(plan.gateIds.length, 'gate')}`;
}

/** The confirm text: what goes, what stays, and what was skipped. */
export function describeUnusedDelete(plan: UnusedDeletePlan): string {
  const lines = [
    `Delete ${count(plan.routeIds.length, 'unused route')} and ${count(plan.gateIds.length, 'unused gate')}?`,
  ];
  if (plan.trimmedGateCount > 0) {
    const gates = plan.trimmedGateCount;
    const routes = plan.trimmedRouteCount;
    lines.push(
      `${gates} of the gates ${gates === 1 ? 'is' : 'are'} on ` +
        `${routes === 1 ? 'a route that stays' : `${routes} routes that stay`} because ` +
        `${routes === 1 ? 'it is' : 'they are'} still in use.`,
    );
  }
  if (plan.skippedRouteCount > 0) {
    const n = plan.skippedRouteCount;
    lines.push(`${count(n, 'selected route')} ${n === 1 ? 'has' : 'have'} nothing unused and will be skipped.`);
  }
  return lines.join('\n');
}
